import React from "react";
import {
  History,
  CheckCircle2,
  XCircle,
  Send,
  Edit3,
  RotateCcw,
  Clock,
  ShieldCheck,
  User,
} from "lucide-react";

/**
 * Converts a UTC timestamp string (e.g. "2026-08-31T13:03:04" or "2026-08-31T05:40:40Z")
 * to the user's localized date and time string.
 */
export function formatUtcToLocal(timestampStr) {
  if (!timestampStr) return "—";
  try {
    let s = String(timestampStr).trim();
    if (!s.endsWith("Z") && !s.includes("+") && !/[+-]\d{2}:\d{2}$/.test(s)) {
      s += "Z";
    }
    const d = new Date(s);
    if (isNaN(d.getTime())) {
      const fallback = new Date(timestampStr);
      if (isNaN(fallback.getTime())) return String(timestampStr);
      return fallback.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    }
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(timestampStr);
  }
}

/**
 * Extracts approval history data array from various API response shapes
 */
export function extractApprovalHistory(responseOrDetail) {
  if (!responseOrDetail) return [];
  const raw = responseOrDetail?.data ?? responseOrDetail;
  if (Array.isArray(raw)) return raw;

  const tabs = Array.isArray(raw?.tabs) ? raw.tabs : [];
  const historyTab = tabs.find(
    (t) =>
      t.step === 8 ||
      t.stepNo === 8 ||
      String(t.name || "").toLowerCase().includes("approval history") ||
      t.id === "approvalHistory"
  );
  if (Array.isArray(historyTab?.data)) return historyTab.data;
  if (Array.isArray(raw?.approvalHistory)) return raw.approvalHistory;
  if (Array.isArray(raw?.approvals)) return raw.approvals;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

const ACTION_CONFIG = {
  APPROVE: {
    label: "Approved",
    icon: CheckCircle2,
    badgeClass: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    iconClass: "text-emerald-600",
  },
  REJECT: {
    label: "Rejected",
    icon: XCircle,
    badgeClass: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
    iconClass: "text-rose-600",
  },
  SUBMIT: {
    label: "Submitted",
    icon: Send,
    badgeClass: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
    iconClass: "text-sky-600",
  },
  UPDATE: {
    label: "Updated",
    icon: Edit3,
    badgeClass: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200",
    iconClass: "text-indigo-600",
  },
  RESUBMIT: {
    label: "Resubmitted",
    icon: RotateCcw,
    badgeClass: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    iconClass: "text-amber-600",
  },
};

export default function ApprovalHistoryTable({ history = [], centerCode = "" }) {
  const items = Array.isArray(history) ? history : [];

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-xs overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/75 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-2xs">
            <History className="h-4.5 w-4.5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Approval History</h3>
            <p className="text-xs text-slate-500">
              Audit trail of all workflow submissions, reviews, and status changes
              {centerCode && ` for ${centerCode}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-200">
            <Clock className="h-3.5 w-3.5" />
            {items.length} Record{items.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto max-h-[420px] rounded-xl border border-slate-200 shadow-xs">
        <table className="w-full text-left text-xs border-separate border-spacing-0">
          <thead className="sticky top-0 z-10 bg-slate-50 shadow-xs">
            <tr className="border-b border-slate-200 bg-slate-50 font-semibold text-slate-600">
              <th className="sticky top-0 bg-slate-50 px-4 py-3 text-center w-12 border-b border-slate-200">#</th>
              <th className="sticky top-0 bg-slate-50 px-4 py-3 border-b border-slate-200">Action</th>
              <th className="sticky top-0 bg-slate-50 px-4 py-3 border-b border-slate-200">Action By</th>
              <th className="sticky top-0 bg-slate-50 px-4 py-3 border-b border-slate-200">Role</th>
              <th className="sticky top-0 bg-slate-50 px-4 py-3 border-b border-slate-200">Timestamp (Local)</th>
              <th className="sticky top-0 bg-slate-50 px-4 py-3 border-b border-slate-200">Remarks</th>
              <th className="sticky top-0 bg-slate-50 px-4 py-3 text-center border-b border-slate-200">Auto Approved</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                  <Clock className="mx-auto h-7 w-7 text-slate-300 mb-2" />
                  <p className="font-medium text-slate-600">No approval history available</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Records will appear here once submitted for verification.
                  </p>
                </td>
              </tr>
            ) : (
              items.map((row, idx) => {
                const actionKey = String(row.action || "").toUpperCase();
                const config = ACTION_CONFIG[actionKey] || {
                  label: row.action || "—",
                  icon: History,
                  badgeClass: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
                  iconClass: "text-slate-500",
                };
                const IconComponent = config.icon;
                const formattedTime = formatUtcToLocal(row.timestamp);
                const actionByName = row.actionByName || row.actionBy || row.actionByCode || "—";
                const roleDisplay = (row.roleCode || "—").replace(/_/g, " ");

                return (
                  <tr key={row.id || idx} className="hover:bg-slate-50/80 transition">
                    <td className="px-4 py-3.5 text-center font-semibold text-slate-400">
                      {idx + 1}
                    </td>
                    <td className="px-4 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${config.badgeClass}`}
                      >
                        <IconComponent className={`h-3.5 w-3.5 ${config.iconClass}`} />
                        <span>{row.action || config.label}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                          <User className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <p className="font-bold text-slate-800">{actionByName}</p>
                          {row.actionByCode && row.actionByCode !== actionByName && (
                            <p className="text-[10px] text-slate-400 font-mono">{row.actionByCode}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-block rounded-md bg-slate-100 px-2 py-0.5 font-medium text-slate-700 text-[11px]">
                        {roleDisplay}
                      </span>
                      {row.approverLevel !== undefined && row.approverLevel !== null && row.approverLevel >= 0 && (
                        <span className="ml-1 text-[10px] text-slate-400">
                          (Lvl {row.approverLevel})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-slate-700 font-medium">
                      {formattedTime}
                    </td>
                    <td className="px-4 py-3.5 max-w-xs text-slate-600">
                      {row.remarks ? (
                        <span className="leading-relaxed">{row.remarks}</span>
                      ) : (
                        <span className="italic text-slate-400">No remarks</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {row.autoApproved ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-700 ring-1 ring-purple-200">
                          <ShieldCheck className="h-3 w-3 text-purple-600" /> Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                          No
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
